/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Box, Grid, List, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { NavBarOcularVisit } from "@components/NavBarOcularVisit";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import ListDocuments from "@components/ListDocuments";
import useInterval from "@hooks/useInterval";
import { STATUS_CATALOG } from "@helpers/constantes";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import { setLoanApplication, setLoanStatus } from "@store/slices/registerSlice";
import { LoanStatus } from "@interfaces/catalogs";
import { useNavigate } from "react-router-dom";
import { DocumentSigner } from "@interfaces/index";
import { useLazyGetContractualDocumentLoanApplicationQuery } from "@api/documents";
import { useLazyGetPersonTypeQuery } from "@api/catalogs";

interface Results {
  person_type_code: string;
  person_type_name: string;
  signers: DocumentSigner[];
}

const SECONDS_TO_FETCH = 120;

const FirmaDocumentos: React.FC = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const application = useSelector((state: RootState) => state.register.application);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const personRepresentante = useSelector((state: RootState) => state.bofu.representante_legal);
  const personAval = useSelector((state: RootState) => state.bofu.aval);

  const [getLoanApplicationById] = useLazyGetLoanApplicationByIdQuery();
  const [getDocumentByApplicationClient] = useLazyGetContractualDocumentLoanApplicationQuery();
  const [getPersonTypes] = useLazyGetPersonTypeQuery();

  const [personTypes, setPersonTypes] = useState<Results[]>([]);

  const loanInterval = useInterval(async () => {
    try {
      const { firma_otros } = STATUS_CATALOG;
      const loan_application = await getLoanApplicationById(application.id).unwrap();
      dispatch(setLoanApplication(loan_application));
      if (loan_application?.status_catalog && loan_application?.status_catalog?.code !== firma_otros.code) {
        dispatch(setLoanStatus(loan_application.status_catalog.code as LoanStatus));
        navigate(loan_application.status_catalog.url_actual);
      }
    } catch (error) {
      console.error("Ha ocurrido un error al obtener la aplicación");
    }
  }, SECONDS_TO_FETCH);

  useEffect(() => {
    (async () => {
      try {
        if (!application.id || !clientDetails.id) {
          throw new Error("Application ID or client ID is not defined");
        }

        const [documents, person_types] = await Promise.all([
          getDocumentByApplicationClient({
            loanApplicationId: application.id,
            clientId: clientDetails.id,
          }).unwrap(),
          getPersonTypes().unwrap(),
        ]);

        // generate array with external signers and pending status
        const external_signers_pending: DocumentSigner[] = [];

        for (const document of documents) {
          if (document.signers_reference && document.signers_reference.length > 0) {
            for (const signer of document.signers_reference) {
              if (signer.type === "external" && signer.status === "pending") {
                const is_added = external_signers_pending.find((s) => s.id === signer.id);
                if (!is_added) {
                  external_signers_pending.push(signer);
                }
              }
            }
          }
        }

        const results: Results[] = [];

        for (const external_signer of external_signers_pending) {
          const is_person_type_added = results.find((p) => p.person_type_code === external_signer.person_type_code);
          if (!is_person_type_added) {
            results.push({
              person_type_code: external_signer.person_type_code,
              person_type_name: person_types.find((p) => p.code === external_signer.person_type_code)?.name || "",
              signers: [external_signer]
            });
          } else {
            is_person_type_added.signers.push(external_signer);
          }
        }

        // order signers by sign_order
        results.forEach((result) => {
          result.signers.sort((a, b) => (a.sign_order || 0) - (b.sign_order || 0));
        });

        setPersonTypes(results);
      } catch (error) {
        console.error("Ha ocurrido un error al obtener los datos");
      }
    })();
  }, [application.id]);

  useEffect(() => {
    loanInterval.setIsActiveInterval(true);

    return () => {
      loanInterval.setIsActiveInterval(false);
    };
  }, []);



  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />

        <Box mx={4}>
          <Typography
            mt={5}
            variant={isMobileOrTablet ? "h5" : "h4"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
            textAlign="center"
          >
            Firma de documentos
          </Typography>

          <NavBarOcularVisit currentStep={4} />

          {personRepresentante && personAval ? (
            <Box>
              <Typography
                mt={4}
                textAlign={"center"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                color={theme?.palette?.primary?.main}
                fontWeight={600}
              >
                ¡La firma de tu empresa se ha registrado con exito!
              </Typography>

              <Typography
                mt={isMobileOrTablet ? 2 : 1}
                textAlign={"center"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                color={theme?.palette?.primary?.main}
                fontWeight={600}
              >
                Ahora enviaremos los documentos para firma electrónica mediante correo electrónico a los firmantes
                adicionales:
              </Typography>

              <Box
                mt={isMobileOrTablet ? 2 : 4}
                sx={{
                  backgroundColor: theme?.palette?.phoneInput?.main,
                  borderRadius: "10px",
                  padding: "20px",
                  maxWidth: "98%",
                }}
              >
                <List sx={{ listStyleType: "disc", paddingLeft: 2 }}>
                  {personTypes.map((personType) => (
                    <ListDocuments
                      title={personType.person_type_name}
                      documents={personType.signers.map((signer) => `${signer.name} - ${signer.email}`)}
                    />
                  ))}
                </List>
              </Box>

              <Typography
                mt={2}
                textAlign={"center"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                color={theme?.palette?.primary?.main}
                fontWeight={600}
              >
                Te recomendamos notificar al los firmantes adicionales para que revisen su correo y completen el proceso de
                firma.
              </Typography>

              <Grid container columnSpacing={1} my={4} justifyContent="center">
                <Grid item xs={6} sm={4} md={3}></Grid>
              </Grid>
            </Box>
          ) : (
            <Box mt={4} display="flex" flexDirection="column" alignItems="center">
              <Skeleton variant="rectangular" sx={{ height: "190px", width: "100%", borderRadius: 2 }} />
            </Box>

          )}
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default FirmaDocumentos;